import { useState, useEffect, useMemo } from 'react'
import type { WordSubmissionProps, WordSubmissionFormData, Category, ForeignWord } from '../../../types/word-submission'
import { Search, AlertCircle, CheckCircle2, Sparkles } from 'lucide-react'
import { transliterateToArabic } from '../../../lib/transliteration'

// =============================================================================
// Sub-Components
// =============================================================================

interface SuccessToastProps {
  show: boolean
}

function SuccessToast({ show }: SuccessToastProps) {
  if (!show) return null
  
  return (
    <div className="fixed z-50 top-20 left-1/2 -translate-x-1/2 animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="flex items-center gap-3 px-6 py-3 bg-amber-500 text-slate-900 rounded-full shadow-lg shadow-amber-500/30">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-medium">تم إرسال الكلمة والبديل بنجاح!</span>
      </div>
    </div>
  )
}

interface PageHeaderProps {
  mode: 'create' | 'edit'
}

function PageHeader({ mode }: PageHeaderProps) {
  return (
    <div className="mb-8 text-center sm:mb-12">
      <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-amber-500 rounded-2xl shadow-lg shadow-amber-500/20">
        <Sparkles className="w-8 h-8 text-slate-900" strokeWidth={2} />
      </div>
      <h1 className="mb-3 text-3xl font-bold text-slate-100 sm:text-4xl">
        {mode === 'edit' ? 'تعديل الكلمة' : 'ضيف كلمة جديدة'}
      </h1>
      <p className="max-w-2xl mx-auto text-lg leading-relaxed text-slate-400">
        {mode === 'edit'
          ? 'عدّل معلومات الكلمة اللي قدمتها. البدائل المقترحة هتفضل زي ما هي.'
          : 'ساعد في بناء قاعدة بيانات الكلمات الأجنبية اللي محتاجة بدائل مصرية. كل مساهمة بتقرّبنا من الحفاظ على لغتنا الأصيلة.'
        }
      </p>
    </div>
  )
}

interface EditModeWarningProps {
  show: boolean
}

function EditModeWarning({ show }: EditModeWarningProps) {
  if (!show) return null
  
  return (
    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 mt-0.5 text-amber-500" />
        <div>
          <div className="mb-1 text-sm font-medium text-amber-400">
            تنبيه: هذه الكلمة لها بدائل مقترحة
          </div>
          <div className="text-xs text-slate-400">
            البدائل المقترحة مش هتتغير، لكن تعديل السياق ممكن يخلي البدائل الموجودة مش مناسبة للمعنى الجديد
          </div>
        </div>
      </div>
    </div>
  )
}

interface ForeignWordInputProps {
  value: string
  error: string
  suggestions: ForeignWord[]
  showSuggestions: boolean
  onChange: (value: string) => void
  onBlur: () => void
  onFocus: () => void
}

function ForeignWordInput({
  value,
  error,
  suggestions,
  showSuggestions,
  onChange,
  onBlur,
  onFocus
}: ForeignWordInputProps) {
  return (
    <div className="relative">
      <label className="block mb-2 text-sm font-semibold text-slate-100">
        الكلمة الأجنبية <span className="text-rose-500">*</span>
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={() => {
            setTimeout(() => {
              // onSuggestionsClose is handled by derived state
            }, 200)
            onBlur()
          }}
          placeholder="مثال: brand, online, screenshot"
          className={`w-full px-4 py-3 pr-11 bg-slate-950 text-slate-100 placeholder-slate-500 rounded-xl border-2 outline-none transition-all ${
            error ? 'border-rose-500' : 'border-slate-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10'
          }`}
        />
        <Search className="absolute w-5 h-5 left-4 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <SuggestionsDropdown suggestions={suggestions} />
      )}

      {error && <FieldError message={error} />}
    </div>
  )
}

interface SuggestionsDropdownProps {
  suggestions: ForeignWord[]
}

function SuggestionsDropdown({ suggestions }: SuggestionsDropdownProps) {
  return (
    <div className="absolute z-10 w-full mt-2 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
      <div className="p-3 border-b border-slate-800">
        <p className="text-xs font-medium text-amber-500">
          الكلمات دي موجودة فعلاً. تأكد إن كلمتك مش مكررة:
        </p>
      </div>
      <div className="overflow-y-auto max-h-48">
        {suggestions.slice(0, 5).map((word) => (
          <div
            key={word.id}
            className="px-4 py-3 border-b border-slate-800 last:border-0 cursor-pointer hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-100">{word.foreignWord}</p>
                <p className="text-sm text-slate-400">{word.arabicTransliteration}</p>
              </div>
              <span className="px-2 py-1 text-xs bg-slate-800 text-slate-400 rounded-md">
                موجودة
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ArabicTransliterationInputProps {
  value: string
  error: string
  onChange: (value: string) => void
  onGenerate: () => void
}

function ArabicTransliterationInput({
  value,
  error,
  onChange,
  onGenerate
}: Omit<ArabicTransliterationInputProps, 'foreignWord'>) {
  return (
    <div>
      <label className="block mb-2 text-sm font-semibold text-slate-100">
        نطق الكلمة بالعربي <span className="text-rose-500">*</span>
        <span className="mr-2 text-xs font-normal text-slate-400">
          (يتم التوليد تلقائياً)
        </span>
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="مثال: براند، أونلاين، سكرين شوت"
          className={`flex-1 px-4 py-3 bg-slate-950 text-slate-100 placeholder-slate-500 rounded-xl border-2 outline-none transition-all ${
            error ? 'border-rose-500' : 'border-slate-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10'
          }`}
        />
        <button
          type="button"
          onClick={onGenerate}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-all"
          title="توليد تلقائي"
        >
          <Sparkles className="w-5 h-5" />
        </button>
      </div>
      {error && <FieldError message={error} />}
    </div>
  )
}

interface CategorySelectorProps {
  categories: Category[]
  selectedId: string
  error: string
  onSelect: (id: string) => void
}

function CategorySelector({ categories, selectedId, error, onSelect }: CategorySelectorProps) {
  return (
    <div>
      <label className="block mb-3 text-sm font-semibold text-slate-100">
        التصنيف <span className="text-rose-500">*</span>
      </label>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => onSelect(category.id)}
            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
              selectedId === category.id
                ? 'bg-amber-500 text-slate-900 shadow-lg shadow-amber-500/30'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>
      {error && <FieldError message={error} />}
    </div>
  )
}

interface AlternativeSectionProps {
  mode: 'create' | 'edit'
  egyptianWord: string
  explanation: string
  errors: Record<string, string>
  onChange: (field: string, value: string) => void
}

function AlternativeSection({ mode, egyptianWord, explanation, errors, onChange }: AlternativeSectionProps) {
  const isDisabled = mode === 'edit'
  
  return (
    <div className={`pt-6 border-t-2 border-slate-800 ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <AlternativeSectionHeader mode={mode} />
      
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-slate-100">
          الكلمة المصرية البديلة <span className="text-rose-500">*</span>
        </label>
        <input
          type="text"
          value={egyptianWord}
          onChange={(e) => onChange('egyptianWord', e.target.value)}
          placeholder="مثال: علامة تجارية، شاشة، متصل..."
          className={`w-full px-4 py-3 bg-slate-950 text-slate-100 placeholder-slate-500 rounded-xl border-2 outline-none transition-all ${
            errors.egyptianWord ? 'border-rose-500' : 'border-slate-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10'
          }`}
        />
        {errors.egyptianWord && <FieldError message={errors.egyptianWord} />}
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-slate-100">
          شرح البديل <span className="text-rose-500">*</span>
          <span className="mr-2 text-xs font-normal text-slate-400">
            (20 حرف على الأقل)
          </span>
        </label>
        <textarea
          value={explanation}
          onChange={(e) => onChange('explanation', e.target.value)}
          placeholder="اشرح ليه البديل المصري ده مناسب وإزاي بيستخدم..."
          rows={4}
          className={`w-full px-4 py-3 bg-slate-950 text-slate-100 placeholder-slate-500 rounded-xl border-2 outline-none transition-all resize-none ${
            errors.explanation ? 'border-rose-500' : 'border-slate-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10'
          }`}
        />
        {errors.explanation && <FieldError message={errors.explanation} />}
      </div>
    </div>
  )
}

interface AlternativeSectionHeaderProps {
  mode: 'create' | 'edit'
}

function AlternativeSectionHeader({ mode }: AlternativeSectionHeaderProps) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="flex items-center justify-center w-10 h-10 bg-amber-500 rounded-full">
        <Sparkles className="w-5 h-5 text-slate-900" />
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-100">
          البديل المقترح
          {mode === 'edit' && <span className="mr-2 text-xs font-normal text-slate-400">(للقراءة فقط)</span>}
        </h3>
        <p className="text-sm text-slate-400">
          {mode === 'edit'
            ? 'البدائل المقترحة لا يمكن تعديلها'
            : 'قدم بديل مصري للكلمة الأجنبية'
          }
        </p>
      </div>
    </div>
  )
}

interface ContextSectionProps {
  usageContext: string
  reasoning: string
  charCounts: { context: number; reasoning: number }
  errors: Record<string, string>
  onChange: (field: string, value: string) => void
}

function ContextSection({ usageContext, reasoning, charCounts, errors, onChange }: ContextSectionProps) {
  return (
    <div className="pt-6 border-t-2 border-slate-800">
      <h3 className="mb-6 text-lg font-bold text-slate-100">السياق والتبرير</h3>

      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-slate-100">
          السياق <span className="text-rose-500">*</span>
          <span className="mr-2 text-xs font-normal text-slate-400">
            ({charCounts.context}/20 حرف على الأقل)
          </span>
        </label>
        <textarea
          value={usageContext}
          onChange={(e) => onChange('usageContext', e.target.value)}
          placeholder="وصّف إزاي الكلمة دي بتستخدم في الحياة اليومية..."
          rows={4}
          className={`w-full px-4 py-3 bg-slate-950 text-slate-100 placeholder-slate-500 rounded-xl border-2 outline-none transition-all resize-none ${
            errors.usageContext ? 'border-rose-500' : 'border-slate-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10'
          }`}
        />
        {errors.usageContext && <FieldError message={errors.usageContext} />}
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-slate-100">
          ليه محتاجين بديل مصري؟ <span className="text-rose-500">*</span>
          <span className="mr-2 text-xs font-normal text-slate-400">
            ({charCounts.reasoning}/20 حرف على الأقل)
          </span>
        </label>
        <textarea
          value={reasoning}
          onChange={(e) => onChange('reasoning', e.target.value)}
          placeholder="اشرح ليه الكلمة دي محتاجة بديل مصري وإيه أهمية ده..."
          rows={4}
          className={`w-full px-4 py-3 bg-slate-950 text-slate-100 placeholder-slate-500 rounded-xl border-2 outline-none transition-all resize-none ${
            errors.reasoning ? 'border-rose-500' : 'border-slate-700 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10'
          }`}
        />
        {errors.reasoning && <FieldError message={errors.reasoning} />}
      </div>
    </div>
  )
}

interface SubmitButtonProps {
  mode: 'create' | 'edit'
}

function SubmitButton({ mode }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-4 px-6 rounded-xl shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
    >
      {mode === 'edit' ? 'حفظ التعديلات' : 'قدّم الكلمة والبديل'}
    </button>
  )
}

function FormFooter() {
  return (
    <div className="px-6 py-5 bg-slate-800/50 border-t border-slate-800 sm:px-8 lg:px-10">
      <p className="text-sm leading-relaxed text-center text-slate-400">
        هيتم مراجعة الكلمات المقدمة من المجتمع. شكراً لمساهمتك في الحفاظ على اللغة المصرية! 🇪🇬
      </p>
    </div>
  )
}

interface FieldErrorProps {
  message: string
}

function FieldError({ message }: FieldErrorProps) {
  return (
    <div className="flex items-center gap-2 mt-2 text-rose-500">
      <AlertCircle className="w-4 h-4" />
      <p className="text-sm font-medium">{message}</p>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function WordSubmission({
  categories,
  existingWords,
  onSubmit,
  onSearchExisting,
  mode = 'create',
  initialData,
  hasAlternatives = false
}: WordSubmissionProps) {
  const [formData, setFormData] = useState<WordSubmissionFormData>({
    foreignWord: initialData?.foreignWord || '',
    arabicTransliteration: initialData?.arabicTransliteration || '',
    categoryId: initialData?.categoryId || '',
    usageContext: initialData?.usageContext || '',
    reasoning: initialData?.reasoning || '',
    egyptianWord: initialData?.egyptianWord || '',
    explanation: initialData?.explanation || ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showSuccess, setShowSuccess] = useState(false)
  const [charCounts, setCharCounts] = useState({
    context: 0,
    reasoning: 0
  })

  // Calculate filtered words using memoization
  const filteredWords = useMemo(() => {
    if (mode === 'edit' || formData.foreignWord.length === 0) {
      return []
    }
    
    return existingWords.filter(word =>
      word.foreignWord.toLowerCase().includes(formData.foreignWord.toLowerCase()) ||
      word.arabicTransliteration.includes(formData.foreignWord)
    )
  }, [formData.foreignWord, existingWords, mode])

  // Derived state for showing suggestions
  const showSuggestions = mode !== 'edit' && filteredWords.length > 0 && formData.foreignWord.length > 0

  // Handle search callback
  useEffect(() => {
    if (mode === 'edit') return // Don't show suggestions in edit mode
    onSearchExisting?.(formData.foreignWord)
  }, [formData.foreignWord, mode, onSearchExisting])

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.foreignWord.trim()) {
      newErrors.foreignWord = 'الكلمة الأجنبية مطلوبة'
    }

    if (!formData.arabicTransliteration.trim()) {
      newErrors.arabicTransliteration = 'التشكيل العربي مطلوب'
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'اختر التصنيف'
    }

    if (!formData.usageContext.trim()) {
      newErrors.usageContext = 'السياق مطلوب'
    } else if (formData.usageContext.trim().length < 20) {
      newErrors.usageContext = 'السياق لازم يكون 20 حرف على الأقل'
    }

    if (!formData.reasoning.trim()) {
      newErrors.reasoning = 'السبب مطلوب'
    } else if (formData.reasoning.trim().length < 20) {
      newErrors.reasoning = 'السبب لازم يكون 20 حرف على الأقل'
    }

    // Skip Egyptian word validation in edit mode
    if (mode === 'create') {
      if (!formData.egyptianWord.trim()) {
        newErrors.egyptianWord = 'البديل المصري مطلوب'
      }

      if (!formData.explanation.trim()) {
        newErrors.explanation = 'شرح البديل مطلوب'
      } else if (formData.explanation.trim().length < 20) {
        newErrors.explanation = 'الشرح لازم يكون 20 حرف على الأقل'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleForeignWordBlur = () => {
    // Only auto-generate in create mode
    if (mode === 'create' && formData.foreignWord && !formData.arabicTransliteration) {
      const generated = transliterateToArabic(formData.foreignWord)
      setFormData(prev => ({ ...prev, arabicTransliteration: generated }))
    }
  }

  const handleGenerateTransliteration = () => {
    if (formData.foreignWord) {
      const generated = transliterateToArabic(formData.foreignWord)
      setFormData(prev => ({ ...prev, arabicTransliteration: generated }))
    }
  }

  const updateField = (field: keyof WordSubmissionFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (validateForm()) {
      onSubmit?.(formData)

      // Only show success and reset form in create mode
      if (mode === 'create') {
        // Show success message
        setShowSuccess(true)
        setTimeout(() => setShowSuccess(false), 4000)

        // Reset form
        setFormData({
          foreignWord: '',
          arabicTransliteration: '',
          categoryId: '',
          usageContext: '',
          reasoning: '',
          egyptianWord: '',
          explanation: ''
        })
        setCharCounts({ context: 0, reasoning: 0 })
        setErrors({})
      }
    }
  }

return (
    <div className="min-h-screen bg-slate-900 pt-16 px-4 py-8 sm:px-6 lg:px-8">
      <SuccessToast show={showSuccess} />
      
      <div className="max-w-3xl mx-auto">
        <PageHeader mode={mode} />
        
        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-3xl shadow-xl overflow-hidden">
          <div className="p-6 space-y-8 sm:p-8 lg:p-10">
            <EditModeWarning show={mode === 'edit' && hasAlternatives} />
            
            <ForeignWordInput
              value={formData.foreignWord}
              error={errors.foreignWord}
              suggestions={filteredWords}
              showSuggestions={showSuggestions}
              onChange={(value) => updateField('foreignWord' as keyof WordSubmissionFormData, value)}
              onBlur={handleForeignWordBlur}
              onFocus={() => {}}
            />
            
            <ArabicTransliterationInput
              value={formData.arabicTransliteration}
              error={errors.arabicTransliteration}
              onChange={(value) => updateField('arabicTransliteration' as keyof WordSubmissionFormData, value)}
              onGenerate={handleGenerateTransliteration}
            />
            
            <CategorySelector
              categories={categories}
              selectedId={formData.categoryId}
              error={errors.categoryId}
              onSelect={(id) => updateField('categoryId' as keyof WordSubmissionFormData, id)}
            />
            
            <AlternativeSection
              mode={mode}
              egyptianWord={formData.egyptianWord}
              explanation={formData.explanation}
              errors={errors}
              onChange={(field, value) => updateField(field as keyof WordSubmissionFormData, value)}
            />
            
            <ContextSection
              usageContext={formData.usageContext}
              reasoning={formData.reasoning}
              charCounts={charCounts}
              errors={errors}
              onChange={(field, value) => {
                updateField(field as keyof WordSubmissionFormData, value)
                if (field === 'usageContext') {
                  setCharCounts(prev => ({ ...prev, context: value.length }))
                } else if (field === 'reasoning') {
                  setCharCounts(prev => ({ ...prev, reasoning: value.length }))
                }
              }}
            />
            
            <SubmitButton mode={mode} />
          </div>
          
          <FormFooter />
        </form>
      </div>
    </div>
  )
}
